import React, { useEffect, useState } from "react";
import { BookOpen, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { listPublished } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import ContributorClassTag from "@/components/ContributorClassTag";

export default function PublishedKnowledge() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    listPublished()
      .then((r) => setItems(r.items || []))
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load published knowledge"));
  }, []);
  return (
    <div className="p-8 md:p-10 space-y-6" data-testid="published-page">
      <div>
        <div className="tracked-caps">Approved outputs</div>
        <h2 className="mt-1 font-serif-display text-3xl">Published knowledge</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Artifacts released through the governed workflow. Clear separation from the candidate queue.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((p) => (
          <article key={p.id} className="rounded-lg border border-border bg-card p-6 hover:shadow-sm transition-shadow" data-testid={`published-${p.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BookOpen className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-mono text-xs text-muted-foreground">{p.id}</span>
                {p.revision && <span className="rounded border border-border bg-muted/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider">{p.revision}</span>}
              </div>
              <StatusBadge value="published" />
            </div>
            <h3 className="mt-3 font-serif-display text-xl leading-snug">{p.title}</h3>
            <div className="mt-1 text-xs text-muted-foreground">{p.domain} · {p.type}</div>

            <dl className="mt-5 space-y-2 text-sm">
              <Row k="Target">{p.publication_target}</Row>
              <Row k="Approval chain">{(p.approval_chain || []).join(" → ")}</Row>
              <Row k="Class">
                <div className="flex gap-1.5 justify-end">
                  {(p.contributor_classes || []).map((c) => <ContributorClassTag key={c} value={c} />)}
                </div>
              </Row>
              <Row k="Evidence"><span className="font-mono text-xs">{Math.round((p.evidence_completeness ?? 1) * 100)}% complete</span></Row>
            </dl>

            <div className="mt-5 flex items-center justify-between">
              <button className="inline-flex items-center gap-1 text-xs text-brand hover:underline">
                View in target <ArrowUpRight className="h-3 w-3" />
              </button>
              <button className="text-xs text-muted-foreground hover:text-foreground">Revision history</button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function Row({ k, children }) {
  return (
    <div className="flex items-baseline justify-between gap-4 hairline-b pb-2 last:border-0 last:pb-0">
      <dt className="tracked-caps">{k}</dt>
      <dd className="text-right">{children}</dd>
    </div>
  );
}
